import { get, post, patch, del, postForm } from '@/lib/api';

/** MANUAL — заведено в кабинете, ERP — приходит из 1С и здесь только читается. */
export type DataSource = 'MANUAL' | 'ERP';

export interface SupplierCategory {
  id: string;
  supplierId: string;
  externalId: string | null;
  name: string;
  sortOrder: number;
  isActive: boolean;
  source: DataSource;
  erpFields: string[];
  createdAt: string;
  updatedAt: string;
}

/** Единица измерения со своей ценой: «л» 80 ₽ и «упак 12 л» 900 ₽ у одного товара. */
export interface ProductUnit {
  id: string;
  externalId: string | null;
  name: string;
  ratio: number | null;
  price: number; // копейки
  vatRate: number;
  packSize: string | null;
  stockQty: number | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  source: DataSource;
  erpFields: string[];
}

export interface SupplierProduct {
  id: string;
  supplierId: string;
  categoryId: string | null;
  externalId: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  // Плоские поля — зеркало основной единицы (units[isDefault]).
  unit: string;
  price: number; // копейки
  vatRate: number;
  packSize: string | null;
  stockQty: number | null;
  isActive: boolean;
  sortOrder: number;
  source: DataSource;
  erpFields: string[];
  units: ProductUnit[];
  createdAt: string;
  updatedAt: string;
}

export type UnitInput = {
  unitId?: string | null;
  name: string;
  ratio?: number | null;
  price: number; // копейки
  vatRate?: number;
  packSize?: string | null;
  stockQty?: number | null;
  isDefault?: boolean;
  isActive?: boolean;
  sortOrder?: number;
};

export type ProductInput = {
  name: string;
  categoryId?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  unit?: string;
  price?: number; // копейки
  vatRate?: number;
  packSize?: string | null;
  stockQty?: number | null;
  isActive?: boolean;
  sortOrder?: number;
  units?: UnitInput[];
  // replace — список единиц полный: отсутствующие в нём гасим.
  unitsMode?: 'merge' | 'replace';
};

export type BulkDeleteResult = {
  requested: number;
  deleted: number;
  deletedItems: { id: string; externalId: string | null; name: string }[];
  deletedExternalIds: string[];
  notFound: string[];
  blocked: { id: string; externalId: string | null; name: string; reason: string }[];
};

// ─── Ставки НДС ───
// Расчётная ставка хранится кодом 100 + процент: 110 — это «10/110».
export const VAT_OPTIONS = [0, 5, 7, 10, 22, 105, 107, 110, 122] as const;

export const vatLabel = (code: number): string => {
  if (code === 0) return 'Без НДС';
  return code >= 100 ? `${code - 100}/${code}` : `${code}%`;
};

/** Старые ставки (20, 20/120) в списке не предлагаем, но в карточке показываем. */
export const vatOptionsFor = (current: number): number[] =>
  VAT_OPTIONS.includes(current as (typeof VAT_OPTIONS)[number])
    ? [...VAT_OPTIONS]
    : [...VAT_OPTIONS, current].sort((a, b) => a - b);

// ─── Что ведёт 1С ───

const DEFAULT_ERP_PRODUCT_FIELDS = [
  'name',
  'externalId',
  'unit',
  'unitId',
  'units',
  'price',
  'vatRate',
  'packSize',
  'stockQty',
];
const DEFAULT_ERP_CATEGORY_FIELDS = ['name', 'externalId'];
// Папку номенклатуры определяет 1С: раскладка, сделанная руками, слетит на
// первом же обмене с категорией. Держим в паре с ALWAYS_LOCKED_PRODUCT_FIELDS
// на бэкенде — сервер такую правку всё равно отклонит с 409.
const ALWAYS_LOCKED_PRODUCT_FIELDS = ['categoryId'];

const locked = (
  row: { source: DataSource; erpFields: string[] },
  fallback: string[],
  always: string[] = [],
): string[] => {
  if (row.source !== 'ERP') return [];
  const base = row.erpFields.length ? row.erpFields : fallback;
  return always.length ? [...new Set([...base, ...always])] : base;
};

export const productLockedFields = (p: SupplierProduct): string[] =>
  locked(p, DEFAULT_ERP_PRODUCT_FIELDS, ALWAYS_LOCKED_PRODUCT_FIELDS);

export const categoryLockedFields = (c: SupplierCategory): string[] =>
  locked(c, DEFAULT_ERP_CATEGORY_FIELDS);

export const isErp = (row: { source: DataSource }): boolean => row.source === 'ERP';

// ─── Категории ───
export const listCategories = () => get<SupplierCategory[]>('/categories');
export const createCategory = (body: { name: string; sortOrder?: number; isActive?: boolean }) =>
  post<SupplierCategory>('/categories', body);
export const updateCategory = (
  id: string,
  body: Partial<{ name: string; sortOrder: number; isActive: boolean }>,
) => patch<SupplierCategory>(`/categories/${id}`, body);
export const deleteCategory = (id: string) => del<{ ok: true }>(`/categories/${id}`);

// ─── Товары ───
export const listProducts = (categoryId?: string) =>
  get<SupplierProduct[]>('/products', categoryId ? { categoryId } : undefined);
export const createProduct = (body: ProductInput) => post<SupplierProduct>('/products', body);
export const updateProduct = (id: string, body: Partial<ProductInput>) =>
  patch<SupplierProduct>(`/products/${id}`, body);
export const deleteProduct = (id: string) => del<{ ok: true }>(`/products/${id}`);
/** Массовое удаление одним запросом вместо пачки одиночных. */
export const deleteProducts = (ids: string[]) => post<BulkDeleteResult>('/products/delete', { ids });

// ─── Загрузка изображений ───
export async function uploadImage(file: File, kind: 'logo' | 'product'): Promise<{ url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  return postForm<{ url: string }>(`/uploads/${kind}`, fd);
}
